import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiSearch, FiFilter, FiUserPlus } from 'react-icons/fi';
import { GUEST_DATA } from '../data/mockData';

export default function GuestList() {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredGuests = GUEST_DATA.filter(g => g.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const getEnergyColor = (energy) => {
    if (energy > 80) return 'bg-green-400 shadow-[0_0_8px_rgba(74,222,128,0.5)]';
    if (energy > 40) return 'bg-[#E5C158] shadow-[0_0_8px_rgba(229,193,88,0.5)]';
    if (energy > 0) return 'bg-red-400 shadow-[0_0_8px_rgba(248,113,113,0.5)]';
    return 'bg-gray-600';
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="h-full flex flex-col pb-20 sm:pb-0"
    >
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Guest Matrix</h1>
          <p className="text-[#94A3B8] text-sm mt-1">{GUEST_DATA.filter(g=>g.status==='Arrived').length} / {GUEST_DATA.length} Checked In</p>
        </div>
        
        <div className="flex w-full md:w-auto gap-3">
          <div className="relative flex-1 md:w-64">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input 
              type="text" 
              placeholder="Search registry..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[#1A1A24] border border-white/10 rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:outline-none focus:border-[#9D4EDD] transition-colors"
            />
          </div>
          <button className="glass-panel p-2 hover:bg-white/5 text-[#94A3B8] transition-colors">
            <SafeIcon icon={FiFilter} />
          </button>
          <button className="bg-[#9D4EDD] hover:bg-[#8a2be2] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2">
            <SafeIcon icon={FiUserPlus} />
            <span className="hidden sm:inline">Add Guest</span>
          </button>
        </div>
      </div>

      <div className="glass-panel flex-1 overflow-hidden flex flex-col">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-white/5 bg-black/20 text-xs uppercase tracking-wider text-[#94A3B8]">
                <th className="p-4 font-medium">Guest Name</th>
                <th className="p-4 font-medium">Status</th>
                <th className="p-4 font-medium">Table Assign</th>
                <th className="p-4 font-medium hidden sm:table-cell">Energy Level</th>
                <th className="p-4 font-medium hidden md:table-cell">Diet/Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filteredGuests.map((guest, idx) => (
                <motion.tr 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: idx * 0.05 }}
                  key={guest.id} 
                  className="hover:bg-white/[0.02] transition-colors group"
                >
                  <td className="p-4">
                    <div className="font-medium text-white group-hover:text-[#9D4EDD] transition-colors">{guest.name}</div>
                    <div className="text-xs text-[#94A3B8] md:hidden mt-1">{guest.note}</div>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs border ${
                      guest.status === 'Arrived' ? 'bg-green-500/10 border-green-500/20 text-green-400' :
                      guest.status === 'Expected' ? 'bg-blue-500/10 border-blue-500/20 text-blue-400' :
                      'bg-red-500/10 border-red-500/20 text-red-400'
                    }`}>
                      {guest.status}
                    </span>
                  </td>
                  <td className="p-4 text-sm font-mono text-[#E5C158]">{guest.table}</td>
                  <td className="p-4 hidden sm:table-cell">
                    <div className="flex items-center gap-2 w-24">
                      <div className="h-1.5 flex-1 bg-black/40 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${getEnergyColor(guest.energy)}`} 
                          style={{ width: `${guest.energy}%` }}
                        />
                      </div>
                      <span className="text-xs text-[#94A3B8] w-6">{guest.energy}%</span>
                    </div>
                  </td>
                  <td className="p-4 hidden md:table-cell text-sm text-[#94A3B8]">
                    {guest.note !== 'None' ? <span className="text-[#E5C158]">{guest.note}</span> : <span className="opacity-50">-</span>}
                  </td>
                </motion.tr>
              ))}
              {filteredGuests.length === 0 && (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-[#94A3B8]">No guests found matching coordinates.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </motion.div>
  );
}